import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Fab,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  DirectionsBus as BusIcon,
  CalendarMonth as CalendarIcon,
  Visibility,
  Delete,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Party } from "../../masterData/party/types";
import { bookingApi } from "../apiService";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import {
  commonButtonProps,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";

// Types for booking data
interface Vehicle {
  id: string;
  status: string;
  companyId: string;
  vehicleType: string;
  acquisitionType: string;
  vehicleNumber: string;
  chassisNumber: string;
  engineNumber: string | null;
  makeYear: number;
  model: string;
  color: string;
  bodyType: string;
  configuration: string;
  fuelType: string;
  seatingCapacity: number;
  isAC: boolean;
}

interface BookingData {
  id: string;
  companyId: string;
  partyId: string;
  vehicleId: string;
  journeyStartDateTime: string;
  journeyEndDateTime: string;
  journeyStartLocation: string;
  journeyEndLocation: string;
  remarks: string | null;
  createdBy: string;
  odometerReading: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  vehicle: Vehicle;
  partyData: Party;
  vehicleType: string;
  marketLoad: MarketLoad;
}

interface VehicleBookings {
  vehicleNumber: string;
  bookings: BookingData[];
}

interface MarketLoad {
  id: string;
  vehicleNumber: string;
  vehicleCompanyName: string;
  ownerName: string;
  ownerContactNumber: string;
  companyName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  gstNumber: string;
  companyId: string;
}

const MarketLoadRentalBookings = ({
  handleEditBooking,
  handleDailyLogs,
  handleNewBooking,
  searchFilters,
  applyFilterTrigger,
}: {
  handleEditBooking: (id: string) => void;
  handleDailyLogs: (
    id: string,
    vehicleId: string,
    vehicleNumber: string
  ) => void;
  handleNewBooking: () => void;
  searchFilters: any;
  applyFilterTrigger: number;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [bookings, setBookings] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRentalBookingsWithFilters = async (
    targetPage: number = page,
    targetLimit: number = rowsPerPage
  ) => {
    setIsLoading(true);
    try {
      // Only pass non-empty filter values
      const activeFilters = {
        ...(searchFilters.vehicleNumber && {
          vehicleNumber: searchFilters.vehicleNumber,
        }),
        ...(searchFilters.startDate && { startDate: searchFilters.startDate }),
        ...(searchFilters.routeFrom && { routeFrom: searchFilters.routeFrom }),
        ...(searchFilters.routeTo && { routeTo: searchFilters.routeTo }),
      };

      const response = await bookingApi.getAllBookings(
        Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
        { page: targetPage + 1, limit: targetLimit }
      );
      setBookings(response.items);
      setTotalCount(response.total);
    } catch (error) {
      console.error("Error fetching rental bookings:", error);
      setBookings([]);
      setTotalCount(0);
    } finally {
      setIsFirstLoad(false);
      setIsLoading(false);
    }
  };

  // Single effect for initial load, pagination, and filter changes
  const prevApplyTriggerRef = React.useRef(applyFilterTrigger);
  useEffect(() => {
    const didFiltersChange = prevApplyTriggerRef.current !== applyFilterTrigger;
    const targetPage = didFiltersChange ? 0 : page;
    fetchRentalBookingsWithFilters(targetPage, rowsPerPage);
    if (didFiltersChange && page !== 0) setPage(0);
    prevApplyTriggerRef.current = applyFilterTrigger;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, applyFilterTrigger]);

  const tableHeaders = [
    "Party Name",
    "Route",
    "Vehicle",
    "Income",
    "Paid",
    "Balance",
    "Expense",
    "Actions",
  ];

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNo = () => {
    setDeleteModalOpen(false);
  };

  const handleYes = async () => {
    try {
      await bookingApi.softDeleteBooking(itemToDelete?.id);
      fetchRentalBookingsWithFilters();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting booking:", error);
    }
  };
  return (
    <>
      <Box sx={{ mb: 4 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              height: "100vh",
            }}
          >
            <CircularProgress
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            />
          </Box>
        ) : bookings?.length === 0 ? (
          <NoBookingsFound handleNewBooking={handleNewBooking} />
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{ "& .MuiTable-root": { borderSpacing: 0 } }}
            >
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    py: 0.5,
                    px: 0.75,
                    fontSize: "0.7rem",
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.25" }}>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          ...tableHeaderStyles,
                          py: 0.375,
                          px: 0.75,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          height: "28px",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((group: any) => (
                    <React.Fragment key={group?.journeyStartDate}>
                      <TableRow>
                        <TableCell
                          colSpan={tableHeaders.length}
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            borderBottomWidth: "0px",
                          }}
                        >
                          {group?.journeyStartDate}
                        </TableCell>
                      </TableRow>
                      {group?.bookings?.map((booking: any) => (
                        <TableRow
                          key={booking?.id}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "action.hover" },
                            height: "36px",
                          }}
                        >
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {booking?.partyData?.partyName}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {booking?.journeyStartLocation} →{" "}
                            {booking?.journeyEndLocation}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            <Box sx={{ lineHeight: 1.2, fontSize: "0.65rem" }}>
                              {formatVehicleNumber(
                                booking?.vehicle?.vehicleNumber
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatCurrency(booking?.income)}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatCurrency(booking?.paid)}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatCurrency(booking?.balance)}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatCurrency(booking?.expense)}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              ...tableCellStyles,
                              py: 0.5,
                              px: 0.75,
                              width: "60px",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDailyLogs(
                                  booking?.id,
                                  booking?.vehicleId,
                                  booking?.marketLoadData?.vehicleNumber
                                )
                              }
                              title="View daily logs"
                              sx={{
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.lighter",
                                },
                                p: 0.25,
                                minHeight: "24px",
                                minWidth: "24px",
                              }}
                            >
                              <Visibility sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setItemToDelete(booking);
                                setDeleteModalOpen(true);
                              }}
                              title="Delete booking"
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  backgroundColor: "error.lighter",
                                },
                                p: 0.25,
                                minHeight: "24px",
                                minWidth: "24px",
                              }}
                            >
                              <Delete sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalCount > 10 && (
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            )}
          </>
        )}
      </Box>

      <DeleteConfirmationDialog
        deleteModalOpen={deleteModalOpen}
        handleNo={handleNo}
        handleYes={handleYes}
        itemToDelete={itemToDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking?"
      />

      {/* Floating Action Button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add booking"
          size="large"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            boxShadow: theme.shadows[6],
          }}
          onClick={handleNewBooking}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}
    </>
  );
};

export default MarketLoadRentalBookings;

export const NoBookingsFound = ({
  handleNewBooking,
}: {
  handleNewBooking: () => void;
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 6,
        textAlign: "center",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <BusIcon sx={{ fontSize: 40, color: "text.disabled", mb: 2 }} />
      <Typography
        variant="h5"
        sx={{
          color: "text.secondary",
          mb: 2,
          fontWeight: 500,
        }}
      >
        No bookings found
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4,
          fontSize: "1rem",
          lineHeight: 1.6,
          maxWidth: 400,
          mx: "auto",
        }}
      >
        Try adjusting your search filters or create a new booking to get started
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleNewBooking}
        {...commonButtonProps}
      >
        Create New Booking
      </Button>
    </Paper>
  );
};
